import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { styled } from "@glitz/react";
import { spacing } from "../theme";
import { Product, Variation } from "../types";
import { useCart } from "../context/CartContext";
import { useViewport } from "../hooks/useViewport";
import ImageGallery from "../components/ImageGallery";
import ProductTitle from "../components/ProductTitle";
import Description from "../components/Description";
import Accordion from "../components/basic/Accordion";
import Button from "../components/basic/Button";
import Price from "../components/Price";
import Base from "../components/basic/Base";
import QuantitySelector from "../components/basic/QuantitySelector";
import AttributeButtonGroup from "../components/basic/AttributeButtonGroup";
import StockLevel from "../components/StockLevel";
import FreeShippingProgress from "../components/FreeShippingBar";
import Loader from "../components/basic/Loader";
import ProductNutritionTags from "../components/ProductNutritionTags";
import BackInStockForm from "../components/BackInStockForm";
import {
  findMatchingProductVariation,
  partialSelectionMatches,
} from "../lib/wooVariationAttribute";
import { trackViewItem } from "../lib/tracking";

type PriceProps = {
  onSale: boolean;
  salePrice: string;
  regularPrice: string;
  price: string;
};

const getRequiredVariationAttributes = (product?: Product) =>
  product?.attributes?.filter(
    (attribute) => attribute.variation && attribute.options?.length > 0
  ) ?? [];

const isAddableStockStatus = (status: string) =>
  status === "instock" || status === "onbackorder";

const ProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { isMobile } = useViewport();
  const [product, setProduct] = useState<Product>();
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const { addToCart } = useCart();
  const [selectedProduct, setSelectedProduct] = useState<Product | Variation>();
  const [productPrice, setProductPrice] = useState<PriceProps>({
    onSale: false,
    salePrice: "",
    regularPrice: "",
    price: "",
  });
  const [selected, setSelected] = useState<{ [key: string]: string }>({});
  const [productVariants, setProductVariants] = useState<Variation[]>([]);
  const [matchingVariant, setMatchingVariant] = useState<Variation | undefined>(
    undefined
  );

  const handleSelect = (name: string, value: string) => {
    setSelected((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAttributeChange = (name: string, value: string) => {
    handleSelect(name, value);
  };

  useEffect(() => {
    fetch(`/api/products/${id}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Produkt kunde inte laddas");
        }
        return response.json();
      })
      .then((data) => setProduct(data))
      .catch((error) => setError(error.message));
  }, [id]);

  useEffect(() => {
    if (product) {
      setProductPrice({
        onSale: product.on_sale,
        salePrice: product.sale_price,
        regularPrice: product.regular_price,
        price: product.price,
      });
      setSelectedProduct(product);
    }
  }, [product]);

  useEffect(() => {
    if (product?.type === "variable") {
      fetch(`/api/product-variations/${product.id}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Produkt kunde inte ladda variant artiklar");
          }
          return response.json();
        })
        .then((data) => setProductVariants(data))
        .catch((error) => setError(error.message));
    }
  }, [product]);

  useEffect(() => {
    if (product?.type !== "variable") {
      setMatchingVariant(undefined);
      return;
    }

    const requiredVariationAttributes = getRequiredVariationAttributes(product);
    const allAttributesSelected =
      requiredVariationAttributes.length > 0 &&
      requiredVariationAttributes.every((attribute) => Boolean(selected[attribute.name]));

    if (allAttributesSelected && productVariants.length !== 0) {
      const selectedVariationAttributes = requiredVariationAttributes.reduce<
        Record<string, string>
      >((attributes, attribute) => {
        attributes[attribute.name] = selected[attribute.name];
        return attributes;
      }, {});
      const match = findMatchingProductVariation(
        selectedVariationAttributes,
        productVariants,
        requiredVariationAttributes
      );
      setMatchingVariant(match || undefined);
    } else {
      setMatchingVariant(undefined);
    }
  }, [selected, productVariants, product]);

  useEffect(() => {
    if (matchingVariant !== undefined) {
      setSelectedProduct(matchingVariant);
      if (matchingVariant.price !== product?.price) {
        setProductPrice({
          onSale: matchingVariant.on_sale,
          salePrice: matchingVariant.sale_price,
          regularPrice: matchingVariant.regular_price,
          price: matchingVariant.price,
        });
      }
    }
  }, [matchingVariant, product?.price]);

  const optionAvailabilityByKey = useMemo(() => {
    const map = new Map<string, boolean>();
    if (!product || product.type !== "variable" || productVariants.length === 0) {
      return map;
    }
    const required = getRequiredVariationAttributes(product);
    for (const attr of required) {
      for (const option of attr.options || []) {
        const key = `${attr.name}\0${option}`;
        const inMatrix = productVariants.some((v) =>
          partialSelectionMatches(v, attr, option, selected, required)
        );
        map.set(key, inMatrix);
      }
    }
    return map;
  }, [product, productVariants, selected]);

  const galleryImages = useMemo(() => {
    if (!product) {
      return [];
    }

    if (
      product.type !== "variable" ||
      matchingVariant === undefined ||
      !matchingVariant.image?.src
    ) {
      return product.images;
    }

    const variantImage = matchingVariant.image;
    const otherImages = product.images.filter((image) => image.src !== variantImage.src);
    return [variantImage, ...otherImages];
  }, [product, matchingVariant]);

  useEffect(() => {
    if (!product) return;
    if (product.type === "variable" && !matchingVariant) return;

    const itemId =
      product.type === "variable" && matchingVariant
        ? matchingVariant.id
        : product.id;
    const nameForItem =
      product.type === "variable" && matchingVariant
        ? `${product.name}${matchingVariant.name}`
        : product.name;
    const priceNum =
      parseFloat(String(productPrice.price).replace(",", ".")) ||
      parseInt(String(productPrice.price), 10) ||
      0;
    if (!itemId || Number.isNaN(priceNum)) return;

    trackViewItem(
      [
        {
          item_id: String(itemId),
          item_name: nameForItem,
          price: priceNum,
          quantity: 1,
        },
      ],
      priceNum
    );
  }, [product, matchingVariant, productPrice.price]);

  const getImageSrc = (sourceProduct = selectedProduct) => {
    let img = "";
    if (sourceProduct) {
      if ("images" in sourceProduct) {
        img = sourceProduct.images[0]?.src;
      } else if ("image" in sourceProduct) {
        img = sourceProduct.image.src;
      }
    }
    return img;
  };

  if (error) {
    return <p>Fel: {error}</p>;
  }

  if (!product) {
    return <Loader ariaLabel="Laddar produkt" />;
  }
  const selectedCartProduct =
    product.type === "variable" ? matchingVariant : selectedProduct;
  const requiredVariationAttributes = getRequiredVariationAttributes(product);
  const allRequiredVariationAttributesSelected =
    product.type !== "variable" ||
    requiredVariationAttributes.every((attribute) => Boolean(selected[attribute.name]));
  const rawInStock = selectedCartProduct && "in_stock" in selectedCartProduct
    ? selectedCartProduct.in_stock
    : product?.in_stock;
  const rawStockStatus = selectedCartProduct && "stock_status" in selectedCartProduct
    ? selectedCartProduct.stock_status
    : product?.stock_status;
  const inStock =
    rawInStock ??
    (rawStockStatus
      ? isAddableStockStatus(rawStockStatus)
      : false);
  const stockStatus = rawStockStatus ?? (inStock ? "instock" : "outofstock");
  const canAddToCartByStock = isAddableStockStatus(stockStatus);
  const canAddToCart =
    selectedCartProduct !== undefined &&
    (product.type !== "variable" ||
      (allRequiredVariationAttributesSelected && matchingVariant !== undefined)) &&
    canAddToCartByStock;
  const addToCartButtonLabel =
    product.type === "variable" && !allRequiredVariationAttributesSelected
      ? "Välj alla val"
      : product.type === "variable" && matchingVariant === undefined
      ? "Variant saknas"
      : !canAddToCartByStock
      ? "Slut i lager"
      : "Lägg till i kundvagn";
  const shippingClass =
    selectedCartProduct && "shipping_class" in selectedCartProduct
      ? selectedCartProduct.shipping_class
      : product?.shipping_class;

  const selectedVariationLabel =
    matchingVariant !== undefined
      ? matchingVariant.attributes
          .map((attr) => attr.option)
          .filter(Boolean)
          .join(", ")
      : "";
  const showBackInStockForm =
    !canAddToCartByStock &&
    (product.type !== "variable" || matchingVariant !== undefined);

  const addOneToCart = () => {
    if (!canAddToCart || !selectedCartProduct) {
      return;
    }

    addToCart({
      id: selectedCartProduct.id,
      sku: selectedCartProduct.sku || product.sku || undefined,
      name:
        product.type === "variable"
          ? product.name + selectedCartProduct.name
          : product?.name,
      price: parseInt(productPrice.price),
      quantity: 1,
      img: getImageSrc(selectedCartProduct),
      in_stock: inStock,
      stock_status: stockStatus,
      shipping_class: shippingClass,
    });
  };

  const addQuantityToCart = () => {
    if (!canAddToCart || !selectedCartProduct) {
      return;
    }

    addToCart({
      id: selectedCartProduct.id,
      sku: selectedCartProduct.sku || product.sku || undefined,
      name:
        product.type === "variable"
          ? product.name + selectedCartProduct.name
          : product?.name,
      price: parseInt(productPrice.price),
      quantity: selectedQuantity,
      img: getImageSrc(selectedCartProduct),
      in_stock: inStock,
      stock_status: stockStatus,
      shipping_class: shippingClass,
    });
  };

  return (
    <div className="product-page-outer">
      <ProductPageWrapper key={product.id}>
        <ProductPageInner className="product-page-wrapper">
          <Left className="product-page-left">
            <ImageSection>
              <ImageGallery images={galleryImages} showThumbnail />
            </ImageSection>
            <Accordion title="Beskrivning" open>
              <Description description={product.description} />
            </Accordion>
          </Left>
          <Right className="product-page-right">
            <ProductTitle left title={product.name} />
            <Price left productPrice={productPrice} />
            <Description description={product.short_description} />
            <ProductNutritionTags
              productId={product.id}
              metaData={product.meta_data}
            />
            <AttributesSection>
              {product?.attributes?.map((attribute) => (
                <AttributeButtonGroup
                  key={attribute.name}
                  name={attribute.name}
                  attribute={attribute}
                  selected={selected}
                  onSelect={handleAttributeChange}
                  isOptionAvailable={
                    product.type === "variable" &&
                    attribute.variation &&
                    productVariants.length > 0
                      ? (option) =>
                          optionAvailabilityByKey.get(
                            `${attribute.name}\0${option}`
                          ) !== false
                      : undefined
                  }
                />
              ))}
            </AttributesSection>
            {!isMobile && (
              <AddToCartSection>
                <QuantitySelector
                  initialQuantity={selectedQuantity}
                  onChange={setSelectedQuantity}
                />
                <Button onClick={addQuantityToCart} disabled={!canAddToCart}>
                  {addToCartButtonLabel}
                </Button>
              </AddToCartSection>
            )}
            <StockLevel
              status={
                product.type === "variable" && matchingVariant === undefined
                  ? "missingVariations"
                  : selectedProduct?.stock_status
              }
            />
            {showBackInStockForm && (
              <BackInStockForm
                productId={product.id}
                productName={product.name}
                variationId={
                  product.type === "variable" && matchingVariant
                    ? matchingVariant.id
                    : undefined
                }
                variationLabel={selectedVariationLabel || undefined}
              />
            )}
            <FreeShippingProgress />
          </Right>
          {isMobile && (
            <StickyAddToCart className="sticky-add-to-cart">
              <Button fullWidth onClick={addOneToCart} disabled={!canAddToCart}>
                {addToCartButtonLabel}
              </Button>
            </StickyAddToCart>
          )}
        </ProductPageInner>
      </ProductPageWrapper>
    </div>
  );
};

const ProductPageWrapper = styled(Base, {
  padding: { xy: spacing.xxxl },
});

const ProductPageInner = styled.div({
  display: "flex",
  flexDirection: "row",
  gap: spacing.xl,
});

const Left = styled.div({
  width: "50%",
});

const Right = styled.div({
  width: "50%",
});

const StickyAddToCart = styled.div({
  display: "none",
});

const AttributesSection = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
});

const ImageSection = styled.div({
  marginBottom: spacing.m,
});

const AddToCartSection = styled.div({
  marginTop: spacing.m,
  display: "flex",
  gap: spacing.m,
});

export default ProductPage;
